import { useEffect } from "react";

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

export function useCursor() {
  useEffect(() => {
    if (isTouchDevice()) return;

    const cursorEl = document.querySelector("[js-cursor]");
    if (!cursorEl) return;

    const titleEl = cursorEl.querySelector(".g-cursor__product-title div");
    const authorEl = cursorEl.querySelector(".g-cursor__product-author div");

    let currentTarget = null;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let rafId = 0;

    const isTransitioning = () =>
      document.body.classList.contains("-transition");

    const show = () => cursorEl.classList.add("-show");
    const hide = () => cursorEl.classList.remove("-show");
    const isPeekOpen = () => document.body.classList.contains("-peek-open");

    const setData = (title, author, isDark) => {
      if (!titleEl || !authorEl) return;
      titleEl.innerHTML = title || "";
      authorEl.innerHTML = author || "";
      if (isDark) cursorEl.classList.add("-dark");
      else cursorEl.classList.remove("-dark");
    };

    const handleOver = (event) => {
      if (isPeekOpen()) {
        currentTarget = null;
        return hide();
      }
      const target = event.target.closest("[data-title]");
      if (!target || target === currentTarget) return;
      if (window.hpview === "grid" && target.closest("[js-grid]")) return;
      if (isTransitioning()) return hide();

      currentTarget = target;
      setData(
        target.getAttribute("data-title"),
        target.getAttribute("data-author"),
        target.hasAttribute("data-theme"),
      );
      show();
    };

    const handleOut = (event) => {
      if (isPeekOpen()) return hide();
      if (!currentTarget) return;
      const related = event.relatedTarget;
      if (related && currentTarget.contains(related)) return;
      currentTarget = null;
      hide();
    };

    const handleMove = (event) => {
      if (isPeekOpen()) {
        currentTarget = null;
        return hide();
      }
      if (event.clientX / window.innerWidth > 0.5)
        cursorEl.classList.add("-right");
      else cursorEl.classList.remove("-right");
      targetX = event.clientX;
      targetY = event.clientY;
      if (window.hpview !== "grid") return;
      const hit = document.elementFromPoint(event.clientX, event.clientY);
      const target = hit && hit.closest && hit.closest("[data-title]");
      if (!target || target === currentTarget) return;
      currentTarget = target;
      setData(
        target.getAttribute("data-title"),
        target.getAttribute("data-author"),
        target.hasAttribute("data-theme"),
      );
      show();
    };

    const updatePosition = () => {
      currentX += (targetX - currentX) * 0.2;
      currentY += (targetY - currentY) * 0.2;
      // Subtract offset to compensate for SearchV2 header/layout
      // Read from CSS variable set by SearchV2 (defaults to 108px if not set)
      const headerOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--searchv2-header-offset')) || 108;
      const offsetY = currentY;
      cursorEl.style.transform = `translate3d(${currentX}px, ${offsetY}px, 0) translateZ(1900px)`;
      rafId = window.requestAnimationFrame(updatePosition);
    };

    document.addEventListener("mouseover", handleOver);
    document.addEventListener("mouseout", handleOut);
    window.addEventListener("mousemove", handleMove);
    rafId = window.requestAnimationFrame(updatePosition);

    return () => {
      document.removeEventListener("mouseover", handleOver);
      document.removeEventListener("mouseout", handleOut);
      window.removeEventListener("mousemove", handleMove);
      window.cancelAnimationFrame(rafId);
    };
  }, []);
}
